import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import * as Icons from 'lucide-react';
import { useAppSystem } from '../hooks/useAppSystem';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { getPendingPayrollRequestsCount } from '../lib/payrollRequests';
import { useUnreadOpportunitiesContext } from '../contexts/UnreadOpportunitiesContext';
import AppSettingsModal from './AppSettingsModal';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { isControlCentreApp } from '../lib/controlCentreHome';

/** Finger travel (px) past which a touch is a scroll, not a tap. */
const TAP_SLOP_PX = 12;

interface LeftNavigationV2Props {
  onToggleOldMenu?: () => void;
  isOpen?: boolean;
  onAppClick?: () => void;
}

export default function LeftNavigationV2({ onToggleOldMenu, isOpen = false, onAppClick }: LeftNavigationV2Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const feedbackReturnHandled = useRef(false);
  const { isUsingAppSystem = false, coreApps = [], installedAddons = [], loading = false, reload } = useAppSystem() || {};
  const { selectedBusiness } = useBusiness();
  const { unreadCount: unreadLeadsCount, refetch: refetchUnreadLeads } = useUnreadOpportunitiesContext();
  const { canAccess } = usePermissionCheck();
  const canViewNexusConnect = canAccess.nexusConnect;
  const canViewAppSettings = canAccess.canViewAppSettings;
  
  // Context menu state
  const [contextMenuApp, setContextMenuApp] = useState<any>(null);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [isLongPressTriggered, setIsLongPressTriggered] = useState(false);
  
  // iPhone-style visual feedback state
  const [selectedAppForFeedback, setSelectedAppForFeedback] = useState<any>(null);
  const [isShowingContextMenu, setIsShowingContextMenu] = useState(false);
  const [selectedAppIconPosition, setSelectedAppIconPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const contextMenuShouldShowRef = useRef(false);
  const appIconRefs = useRef<{ [key: string]: HTMLAnchorElement | null }>({});
  const touchStartTimeRef = useRef<number | null>(null);
  /** Where the current touch began, and whether it has travelled far enough to
   *  count as a scroll. Navigation happens on touchend, so without this a
   *  scroll that starts on an app icon would open that app. */
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const touchMovedRef = useRef(false);
  const lastLongPressTimeRef = useRef<number | null>(null);
  
  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsApp, setSettingsApp] = useState<any>(null);
  
  // Listen for openAppSettings event from search
  useEffect(() => {
    const handleOpenAppSettings = (event: CustomEvent) => {
      if (!canViewAppSettings) {
        return;
      }
      const { appSlug, openServiceAreas, openFeedbackForm } = event.detail || {};
      if (appSlug) {
        // Find the app
        const allApps = [...coreApps, ...installedAddons];
        const targetApp = allApps.find(app => app.app?.slug === appSlug);
        if (targetApp) {
          if (openServiceAreas) {
            // Set flag to auto-open service areas modal
            (window as any).__pendingServiceAreasOpen = true;
          }
          if (openFeedbackForm) {
            (window as any).__pendingFeedbackFormOpen = true;
          }
          setSettingsApp(targetApp);
          setShowSettingsModal(true);
        }
      }
    };
    
    window.addEventListener('openAppSettings', handleOpenAppSettings as EventListener);
    return () => {
      window.removeEventListener('openAppSettings', handleOpenAppSettings as EventListener);
    };
  }, [coreApps, installedAddons, canViewAppSettings]);

  // Google sign-in started from the feedback form returns here and reopens it.
  useEffect(() => {
    if (feedbackReturnHandled.current || !canViewAppSettings) return;
    const params = new URLSearchParams(location.search);
    let flagged = false;
    try {
      flagged = sessionStorage.getItem('feedback_gbp_connect') === '1';
    } catch {
      /* ignore */
    }
    if (params.get('feedback_connect') !== '1' && !flagged) return;
    const apps = [...coreApps, ...installedAddons];
    if (apps.length === 0) return;
    const calendar =
      apps.find((app) => app.app?.slug === 'calendar') ||
      apps.find((app) => app.app?.slug === 'mobile-calendar');
    if (!calendar?.app?.slug) return;
    feedbackReturnHandled.current = true;
    (window as any).__pendingFeedbackFormOpen = true;
    window.dispatchEvent(
      new CustomEvent('openAppSettings', {
        detail: { appSlug: calendar.app.slug, openFeedbackForm: true },
      }),
    );
  }, [location.search, coreApps, installedAddons, canViewAppSettings]);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editModeApps, setEditModeApps] = useState<any[]>([]);
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [selectedAppPosition, setSelectedAppPosition] = useState<number | null>(null);
  
  // Hidden apps visibility state
  const [showHiddenApps, setShowHiddenApps] = useState(false);
  
  // Payroll badge count state
  const [pendingPayrollCount, setPendingPayrollCount] = useState(0);

  // Reset hidden apps visibility when menu closes
  useEffect(() => {
    if (!isOpen) {
      setShowHiddenApps(false);
    }
  }, [isOpen]);

  // Load pending payroll requests count
  useEffect(() => {
    const loadPendingCount = async () => {
      if (!selectedBusiness?.id) return;
      try {
        const count = await getPendingPayrollRequestsCount(selectedBusiness.id);
        setPendingPayrollCount(count);
      } catch (error) {
        console.error('Error loading pending payroll count:', error);
      }
    };

    loadPendingCount();

    // Set up real-time listener for payroll_request table changes
    const channel = supabase
      .channel('payroll_requests_leftnav')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_request',
          filter: `business_id=eq.${selectedBusiness?.id}`
        },
        () => {
          loadPendingCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id]);

  // Refresh badge counts when menu opens (fallback method)
  useEffect(() => {
    const loadPendingCount = async () => {
      if (!selectedBusiness?.id) return;
      try {
        const count = await getPendingPayrollRequestsCount(selectedBusiness.id);
        setPendingPayrollCount(count);
      } catch (error) {
        console.error('Error loading pending payroll count:', error);
      }
    };

    if (isOpen) {
      loadPendingCount();
      // Also refresh CRM unread leads count
      refetchUnreadLeads();
    }
  }, [isOpen, selectedBusiness?.id]);
  
  // Helper function to check if user has app access based on app slug
  const hasAppAccess = (appSlug: string | undefined): boolean => {
    if (!appSlug) return true; // If no slug, allow access (fallback)
    
    // Map app slugs to permission checks
    // Only include apps that have "App Access" permissions configured in ResourcePermissions
    const permissionMap: { [key: string]: boolean } = {
      // Calendar apps
      'calendar': canAccess.calendar,
      'mobile-calendar': canAccess.calendar, // Mobile calendar uses same permission as calendar
      'm-calendar': canAccess.calendar,
      
      // Core apps with App Access
      'messages': canAccess.messages,
      'marketing': canAccess.marketing,
      'crm': canAccess.crm,
      'payroll': canAccess.payroll,
      'supplies': canAccess.supplies,
      'services': canAccess.services,
      'teams': canAccess.resources, // Teams app uses resources permission
      'tasks': canAccess.tasks,
      'bookings': canAccess.bookings,
      'proposals': canAccess.proposals,
      'customers': canAccess.customers,
      'analytics': canAccess.kpiDashboard, // KPI Dashboard app uses 'analytics' slug
      'kpi-dashboard': canAccess.kpiDashboard, // Legacy slug support
      'kpiDashboard': canAccess.kpiDashboard, // Legacy camelCase variant support
      'automations': canAccess.automations,
      'projects': canAccess.projects,
      
      // Inventory app (separate from supplies)
      'inventory': canAccess.inventory,
      
      // Invoices app
      'invoices': canAccess.invoices,
      
      // Products app
      'products': canAccess.products,
      
      // Gift Cards app
      'gift-cards': canAccess.giftCards,
      
      // Expenses app — visible only when both enabled and appVisibility is on
      'expenses': canAccess.expenses && canAccess.expensesAppVisibility,
    };
    
    // Check if we have a permission mapping for this app
    if (permissionMap.hasOwnProperty(appSlug)) {
      return permissionMap[appSlug];
    }
    
    // Default to true for apps without specific permission checks (backward compatibility)
    // This allows apps that don't have permission checks yet to still show
    return true;
  };

  // Combine all apps and filter by permissions, then separate visible/hidden.
  // Control Centre is a universal core surface (FAB button), never a launcher app.
  const allApps = [...coreApps, ...installedAddons]
    .filter(app => !isControlCentreApp(app.app))
    .filter(app => hasAppAccess(app.app?.slug)); // Filter out apps user doesn't have access to
  
  const visibleApps = allApps
    .filter(app => app.visible_app_grid !== null && app.visible_app_grid !== undefined)
    .sort((a, b) => (a.visible_app_grid || 0) - (b.visible_app_grid || 0));
  
  const hiddenApps = allApps
    .filter(app => app.hidden_app_grid !== null && app.hidden_app_grid !== undefined)
    .sort((a, b) => (a.hidden_app_grid || 0) - (b.hidden_app_grid || 0));

  // Create visible grid array
  const createVisibleGrid = () => {
    const appsToUse = isEditMode ? editModeApps.filter(app => app.visible_app_grid !== null && app.visible_app_grid !== undefined) : visibleApps;
    const maxPosition = appsToUse.length > 0 ? Math.max(...appsToUse.map(app => app.visible_app_grid || 0)) : 0;
    
    // In edit mode, add an extra row (3 slots) below the lowest app
    // In normal mode, only create slots up to the highest app position + 1
    const gridSize = isEditMode ? maxPosition + 4 : maxPosition + 1;
    const grid = Array(gridSize).fill(null);
    
    appsToUse.forEach(app => {
      const position = app.visible_app_grid;
      if (position !== null && position !== undefined && position >= 0) {
        grid[position] = app;
      }
    });
    
    return grid;
  };

  // Create hidden grid array
  const createHiddenGrid = () => {
    // Show hidden grid in edit mode OR when showHiddenApps is true in normal mode
    if (!isEditMode && !showHiddenApps) return [];
    
    const hiddenAppsToUse = isEditMode 
      ? editModeApps.filter(app => app.hidden_app_grid !== null && app.hidden_app_grid !== undefined)
      : hiddenApps;
    
    const maxPosition = hiddenAppsToUse.length > 0 ? Math.max(...hiddenAppsToUse.map(app => app.hidden_app_grid || 0)) : 0;
    
    // Add an extra row for hidden apps in edit mode, but only if there are hidden apps
    // In normal mode, just show the apps without extra slots
    const gridSize = isEditMode 
      ? (hiddenAppsToUse.length > 0 ? maxPosition + 4 : 3)
      : (hiddenAppsToUse.length > 0 ? maxPosition + 1 : 0);
    
    const grid = Array(gridSize).fill(null);
    
    hiddenAppsToUse.forEach(app => {
      const position = isEditMode ? app.hidden_app_grid : app.hidden_app_grid;
      if (position !== null && position !== undefined && position >= 0) {
        grid[position] = app;
      }
    });
    
    return grid;
  };

  // Enter edit mode
  const enterEditMode = () => {
    setIsEditMode(true);
    setEditModeApps([...visibleApps, ...hiddenApps]); // Copy current apps for editing
    setContextMenuApp(null);
  };

  // Exit edit mode without saving
  const exitEditMode = () => {
    setIsEditMode(false);
    setEditModeApps([]);
    setSelectedApp(null);
    setSelectedAppPosition(null);
  };

  // Handle app selection in edit mode
  const handleAppSelect = (app: any, position: number) => {
    if (!isEditMode) return;
    
    if (selectedApp && selectedApp.app?.id === app.app?.id) {
      // Deselect if clicking the same app
      setSelectedApp(null);
      setSelectedAppPosition(null);
    } else {
      // Select the app
      setSelectedApp(app);
      setSelectedAppPosition(position);
    }
  };

  // Handle empty slot click in edit mode
  const handleEmptySlotClick = (targetPosition: number, gridType: 'visible' | 'hidden') => {
    if (!isEditMode || !selectedApp || selectedAppPosition === null) return;
    
    // Move the selected app to the target position
    const updatedApps = editModeApps.map(app => {
      if (app.app?.id === selectedApp.app?.id) {
        if (gridType === 'visible') {
          return { 
            ...app, 
            visible_app_grid: targetPosition,
            hidden_app_grid: null // Clear hidden position
          };
        } else {
          return { 
            ...app, 
            hidden_app_grid: targetPosition,
            visible_app_grid: null // Clear visible position
          };
        }
      }
      // If there's an app at the target position, move it to the source position
      const targetColumn = gridType === 'visible' ? 'visible_app_grid' : 'hidden_app_grid';
      if (app[targetColumn] === targetPosition) {
        // Move the existing app to where the selected app came from
        const sourceColumn = selectedApp.visible_app_grid !== null ? 'visible_app_grid' : 'hidden_app_grid';
        const sourcePosition = selectedApp.visible_app_grid !== null ? selectedApp.visible_app_grid : selectedApp.hidden_app_grid;
        
        if (sourceColumn === 'visible_app_grid') {
          return {
            ...app,
            visible_app_grid: sourcePosition,
            hidden_app_grid: null
          };
        } else {
          return {
            ...app,
            hidden_app_grid: sourcePosition,
            visible_app_grid: null
          };
        }
        }
        return app;
      });

    setEditModeApps(updatedApps);
    setSelectedApp(null);
    setSelectedAppPosition(null);
  };

  // Save layout changes
  const saveLayout = async () => {
    try {
      // Update database with new positions
      const updatePromises = editModeApps.map(app => 
        supabase
          .from('business_apps_v2')
          .update({ 
            visible_app_grid: app.visible_app_grid,
            hidden_app_grid: app.hidden_app_grid
          })
          .eq('id', app.id || app.app?.id)
      );

      await Promise.all(updatePromises);
      
      // Exit edit mode
      setIsEditMode(false);
      setEditModeApps([]);
      setSelectedApp(null);
      setSelectedAppPosition(null);
      
      // Reload only the app system data instead of full page reload
      if (reload) {
        await reload();
      }
      
    } catch (error) {
      console.error('Error saving layout:', error);
    }
  };

  const visibleGridApps = createVisibleGrid();
  const hiddenGridApps = createHiddenGrid();

  // Long press handlers
  const handleLongPressStart = (app: any) => {
    if (!canViewAppSettings) {
      return;
    }
    console.log('🟢 LONG PRESS START');
    
    // Record when touch started
    touchStartTimeRef.current = Date.now();
    
    // Get the app icon element position
    const appIconElement = appIconRefs.current[app.app.id];
    if (appIconElement) {
      const rect = appIconElement.getBoundingClientRect();
      setSelectedAppIconPosition({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    }
    
    // Start iPhone-style visual feedback immediately
    setSelectedAppForFeedback(app);
    
    const timer = setTimeout(() => {
      console.log('🟡 TIMER TRIGGERED - Setting contextMenuShouldShowRef to TRUE');
      
      // Set the ref first (synchronously), then the state
      contextMenuShouldShowRef.current = true;
      setIsLongPressTriggered(true);
      setIsShowingContextMenu(true);
      
      // No need for menu positioning - buttons will be positioned relative to app icon
      setContextMenuApp({ ...app, timestamp: Date.now() });
    }, 500); // 500ms long press duration
    
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    console.log('🔴 LONG PRESS END - contextMenuShouldShowRef.current:', contextMenuShouldShowRef.current);
    // Only clear the timer and reset if the context menu isn't already showing
    if (!contextMenuShouldShowRef.current && !isShowingContextMenu) {
      console.log('⚪ Menu NOT showing - clearing timer and resetting');
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
      
      // Reset immediately so navigation can proceed
      setIsLongPressTriggered(false);
      setSelectedAppForFeedback(null);
      setSelectedAppIconPosition(null);
    } else {
      console.log('🟣 Menu IS showing - doing nothing (keeping menu open)');
    }
  };

  const handleAppClick = (e: React.MouseEvent) => {
    // If long press was triggered and menu is showing, prevent navigation
    if (isLongPressTriggered || contextMenuShouldShowRef.current) {
      e.preventDefault();
      setContextMenuApp(null);
      setSelectedAppForFeedback(null);
      setSelectedAppIconPosition(null);
      setIsShowingContextMenu(false);
      setIsLongPressTriggered(false);
      contextMenuShouldShowRef.current = false;
      return false;
    }
    
    // Close the sidebar when app is clicked
    if (onAppClick) {
      onAppClick();
    }
    
    // Normal navigation will proceed
  };

  // Close context menu when clicking outside
  const handleClickOutside = (event: MouseEvent | TouchEvent) => {
    // Ignore touch events - only handle mouse clicks
    if (event.type === 'touchend' || event.type === 'touchstart' || event.type === 'touchcancel') {
      return;
    }
    
    console.log('❌ CLICK OUTSIDE - Checking if this is a valid outside click');
    
    const target = event.target as Element;
    
    // Check if click is on the floating buttons container
    const buttonsContainer = target.closest('[data-floating-buttons]');
    if (buttonsContainer) {
      console.log('🎯 Click is on floating buttons - ignoring');
      return;
    }
    
    // Check if click is on the selected app icon (portal version)
    const selectedAppIcon = target.closest('[data-selected-app-icon]');
    if (selectedAppIcon) {
      console.log('🎯 Click is on selected app icon - ignoring');
      return;
    }
    
    // Check if this click happened too soon after the menu appeared (ignore mouse release clicks)
    const timeSinceMenuAppeared = Date.now() - (contextMenuApp?.timestamp || 0);
    if (timeSinceMenuAppeared < 500) {
      console.log('🎯 Click happened too soon after menu appeared - ignoring');
      return;
    }
    
    console.log('✅ Valid outside click - closing menu');
    setContextMenuApp(null);
    setSelectedAppForFeedback(null);
    setSelectedAppIconPosition(null);
    setIsShowingContextMenu(false);
    setIsLongPressTriggered(false);
    contextMenuShouldShowRef.current = false;
  };

  // Add click outside listener when menu is showing
  React.useEffect(() => {
    if (isShowingContextMenu) {
      console.log('🎯 Adding smart click outside listener');
      // Only listen for mouse clicks, not touch events
      document.addEventListener('click', handleClickOutside);
      
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isShowingContextMenu]);

  // Handle visual feedback timing
  React.useEffect(() => {
    if (selectedAppForFeedback && !isShowingContextMenu) {
      // Add a subtle haptic feedback simulation
      if (navigator.vibrate) {
        navigator.vibrate(50); // Short vibration
      }
    }
  }, [selectedAppForFeedback, isShowingContextMenu]);

  // Get display name for app (handles name mappings)
  const getAppDisplayName = (app: any) => {
    if (!app || !app.app) return '';
    
    // Handle specific name mappings
    const nameMap: { [key: string]: string } = {
      'mobile-calendar-core-app': 'Field-Pro',
      'm-calendar-core-app': 'Field-Pro',
      'automations-core-app': 'Automate',
      'products-addon-app': 'Store',
      'payroll-addon-app': 'T-Sheets',
    };
    
    // Check if we have a mapping for the app ID
    if (app.app.id && nameMap[app.app.id]) {
      return nameMap[app.app.id];
    }
    
    // Check if we have a mapping for the slug
    if (app.app.slug) {
      if (app.app.slug === 'mobile-calendar' || app.app.slug === 'm-calendar') {
        return 'Field-Pro';
      }
      if (app.app.slug === 'automations') {
        return 'Automate';
      }
      if (app.app.slug === 'products') {
        return 'Store';
      }
      if (app.app.slug === 'projects') {
        return 'Projects';
      }
      if (app.app.slug === 'payroll') {
        return 'T-Sheets';
      }
    }
    
    // Fall back to the name from the database
    return app.app.name || '';
  };

  // Custom PNG app icons (keyed by slug)
  const customAppIcons: { [key: string]: string } = {
    'analytics': '/Analyticsappicon.png',
    'projects': '/Projectsappicon.png',
    'calendar': '/Calenderappicon.png',
    'crm': '/CRMappicon.png',
    'proposals': '/Proposalsappicon.png',
    'tasks': '/Bookingsappicon.png',
    'inventory': '/Inventoryappicon.png',
    'expenses': '/Expensesappicon.png',
    'payroll': '/Timesheetsappicon.png',
    'teams': '/Teamsappicon.png',
    'invoices': '/Invoicesappicon.png',
    'automations': '/Automateappicon.png',
    'services': '/Servicesappicon.png',
    'customers': '/Customersappicon.png',
    'marketing': '/Marketingappicon.png',
    'gift-cards': '/Giftcardsappicon.png',
    'messages': '/Messagesappicon.png',
    'bookings': '/Bookingsappicon.png',
    'products': '/Storeappicon.png',
    'mobile-calendar': '/Fieldproappicon.png',
    'm-calendar': '/Fieldproappicon.png',
  };

  // Placeholder icons for different app types
  const getPlaceholderIcon = (appSlug: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'calendar': <Icons.Calendar className="h-6 w-6" />,
      'mobile-calendar': <Icons.Smartphone className="h-6 w-6" />,
      'm-calendar': <Icons.Smartphone className="h-6 w-6" />,
      'customers': <Icons.Users className="h-6 w-6" />,
      'automations': <Icons.Zap className="h-6 w-6" />,
      'teams': <Icons.Users className="h-6 w-6" />,
      'tasks': <Icons.ListChecks className="h-6 w-6" />,
      'analytics': <Icons.BarChart3 className="h-6 w-6" />,
      'crm': <Icons.Building2 className="h-6 w-6" />,
      'inventory': <Icons.Package className="h-6 w-6" />,
      'payroll': <Icons.DollarSign className="h-6 w-6" />,
      'services': <Icons.Wrench className="h-6 w-6" />,
      'bookings': <Icons.CalendarDays className="h-6 w-6" />,
      'proposals': <Icons.FileText className="h-6 w-6" />,
      'marketing': <Icons.Megaphone className="h-6 w-6" />,
      'invoices': <Icons.Receipt className="h-6 w-6" />,
      'messages': <Icons.MessageSquare className="h-6 w-6" />,
      'products': <Icons.ShoppingCart className="h-6 w-6" />,
      'projects': <Icons.Folder className="h-6 w-6" />,
      'que': <Icons.ListChecks className="h-6 w-6" />,
    };
    
    return iconMap[appSlug] || <Icons.Package className="h-6 w-6" />;
  };

  // Update app icon position when scrolling or resizing
  useEffect(() => {
    if (!selectedAppForFeedback || !selectedAppIconPosition) return;

    const updatePosition = () => {
      const appIconElement = appIconRefs.current[selectedAppForFeedback.app.id];
      if (appIconElement) {
        const rect = appIconElement.getBoundingClientRect();
        setSelectedAppIconPosition({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [selectedAppForFeedback, selectedAppIconPosition]);

  // Render a grid (visible or hidden)
  const renderGrid = (gridApps: any[], gridType: 'visible' | 'hidden') => {
    // Calculate animation delay based on position (staggered entrance)
    const getAnimationDelay = (index: number) => {
      if (!isOpen) return 0;
      // Stagger by row and column for a wave effect
      const row = Math.floor(index / 3);
      const col = index % 3;
      return (row * 0.05) + (col * 0.03);
    };

    return (
      <div className="grid grid-cols-3 gap-x-5 gap-y-4 sm:gap-x-4 sm:gap-y-3 justify-items-center sm:justify-items-stretch relative" style={{ zIndex: 999999 }}>
        {gridApps.map((app, index) => (
          <div 
            key={`${gridType}-${index}`} 
            className="flex flex-col items-center w-[82px] sm:w-auto"
            style={{
              animation: isOpen && !isEditMode 
                ? `iosAppIconEnter 0.4s cubic-bezier(0.4, 0.0, 0.2, 1) ${getAnimationDelay(index)}s both`
                : 'none',
              position: 'relative',
              zIndex: selectedAppForFeedback && app && selectedAppForFeedback.app?.id === app.app?.id ? 1000000 : 'auto',
            }}
          >
            {app ? (
              <>
              <Link
                ref={(el) => {
                  appIconRefs.current[app.app.id] = el;
                }}
                to={isEditMode ? '#' : (app.app.route_prefix || '#')}
                data-app-id={app.app.id}
                className={`aspect-square w-full rounded-[22%] flex flex-col items-center justify-center transition-all duration-200 ease-out ${
                  customAppIcons[app.app.slug] ? 'p-0 overflow-hidden bg-white' : 'bg-gradient-to-br from-blue-600 to-blue-500 p-1.5 sm:p-2'
                } cursor-pointer ${
                  isEditMode && selectedApp && selectedApp.app?.id === app.app?.id ? 'ring-4 ring-blue-400 ring-opacity-75' : ''
                } ${
                  selectedAppForFeedback && selectedAppForFeedback.app?.id === app.app?.id 
                    ? 'opacity-0' 
                    : 'hover:-translate-y-1 active:scale-95'
                }`}
                style={{
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                  touchAction: 'manipulation',
                  position: 'relative',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.08)',
                  ...(selectedAppForFeedback && selectedAppForFeedback.app?.id === app.app?.id ? {
                    zIndex: 1000000,
                    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transform: 'scale(1.25)',
                  } : {
                    zIndex: 999999,
                  })
                }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (isEditMode) {
                  handleAppSelect(app, index);
                  return;
                }
                
                const linkElement = e.currentTarget.closest('a');
                const timeSinceLongPress = lastLongPressTimeRef.current ? Date.now() - lastLongPressTimeRef.current : Infinity;
                const isLongPressClick = linkElement?.hasAttribute('data-long-press-active') || 
                                        contextMenuShouldShowRef.current || 
                                        isShowingContextMenu ||
                                        timeSinceLongPress < 600;
                
                if (isLongPressClick) {
                  return;
                }

                if (touchMovedRef.current) {
                  touchMovedRef.current = false;
                  return;
                }
                
                if (navigator.vibrate) {
                  navigator.vibrate(10);
                }

                const route = app.app.route_prefix;
                
                handleAppClick(e);

                if (route && route !== '#') {
                  navigate(route);
                }
              }}
              onMouseDown={(e) => {
                if (isEditMode) {
                  e.preventDefault();
                  return;
                }
                // Immediate visual feedback
                e.currentTarget.style.transform = 'scale(0.92)';
                // Don't prevent default on mouse down - allow normal clicks
                handleLongPressStart(app);
              }}
              onMouseUp={isEditMode ? undefined : (e) => {
                console.log('🖱️ MOUSE UP');
                e.currentTarget.style.transform = '';
                e.stopPropagation();
                handleLongPressEnd();
              }}
              onMouseLeave={isEditMode ? undefined : (e) => {
                console.log('🖱️ MOUSE LEAVE');
                e.currentTarget.style.transform = '';
                e.stopPropagation();
                handleLongPressEnd();
              }}
              onTouchStart={(e) => {
                if (isEditMode) {
                  return;
                }

                const touch = e.touches[0];
                touchStartPosRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
                touchMovedRef.current = false;

                // Prevent default to capture touch event for long press detection
                e.preventDefault();
                
                // Immediate visual feedback on touch
                e.currentTarget.style.transform = 'scale(0.95)';
                
                // Start long press timer
                handleLongPressStart(app);
              }}
              onTouchMove={(e) => {
                if (isEditMode || touchMovedRef.current) return;
                // A long press already opened the context menu — leave it alone.
                if (contextMenuShouldShowRef.current || isShowingContextMenu) return;
                const start = touchStartPosRef.current;
                const touch = e.touches[0];
                if (!start || !touch) return;
                const dx = touch.clientX - start.x;
                const dy = touch.clientY - start.y;
                if (Math.sqrt(dx * dx + dy * dy) < TAP_SLOP_PX) return;

                // The finger travelled: this is a scroll. Drop the press state so
                // touchend won't navigate and the long press won't fire.
                touchMovedRef.current = true;
                e.currentTarget.style.transform = '';
                if (longPressTimer) {
                  clearTimeout(longPressTimer);
                  setLongPressTimer(null);
                }
                setIsLongPressTriggered(false);
                setSelectedAppForFeedback(null);
                setSelectedAppIconPosition(null);
                touchStartTimeRef.current = null;
              }}
              onTouchEnd={(e) => {
                if (!isEditMode) {
                  e.currentTarget.style.transform = '';

                  // Scrolled across this icon — swallow the tap.
                  if (touchMovedRef.current) {
                    touchMovedRef.current = false;
                    touchStartTimeRef.current = null;
                    if (longPressTimer) {
                      clearTimeout(longPressTimer);
                      setLongPressTimer(null);
                    }
                    return;
                  }
                  
                  // CRITICAL: Check if enough time has passed for long press
                  const touchDuration = touchStartTimeRef.current ? Date.now() - touchStartTimeRef.current : 0;
                  const wasLongPress = touchDuration >= 500 || contextMenuShouldShowRef.current;
                  
                  console.log('🔵 TOUCH END (PWA) - duration:', touchDuration, 'wasLongPress:', wasLongPress, 'ref:', contextMenuShouldShowRef.current);
                  
                  if (wasLongPress) {
                    // Long press was successful - prevent navigation and keep menu open
                    console.log('✅ Long press detected - keeping menu open, preventing navigation AND click event');
                    e.preventDefault();
                    e.stopPropagation();
                    
                    // Record the time of this long press to ignore subsequent clicks
                    lastLongPressTimeRef.current = Date.now();
                    
                    // CRITICAL for PWA: Prevent the touch event from triggering a click event
                    // In PWA, touchEnd can trigger a click event which would close the menu
                    // We need to mark this so the onClick handler knows to ignore it
                    const linkElement = e.currentTarget.closest('a');
                    if (linkElement) {
                      // Add a data attribute to mark this as a long press
                      linkElement.setAttribute('data-long-press-active', 'true');
                      // Remove it after a delay to allow normal clicks later
                      setTimeout(() => {
                        linkElement.removeAttribute('data-long-press-active');
                      }, 1000);
                    }
                    
                    // Clear timer but DON'T reset any state - menu stays open
                    if (longPressTimer) {
                      clearTimeout(longPressTimer);
                      setLongPressTimer(null);
                    }
                    
                    // Reset touch start time
                    touchStartTimeRef.current = null;
                    return;
                  }
                  
                  // It was a normal tap - clear timer and trigger navigation
                  console.log('✅ Normal tap - clearing timer and navigating');
                  if (longPressTimer) {
                    clearTimeout(longPressTimer);
                    setLongPressTimer(null);
                  }
                  
                  setIsLongPressTriggered(false);
                  setSelectedAppForFeedback(null);
                  setSelectedAppIconPosition(null);
                  touchStartTimeRef.current = null;
                  
                  // Navigate directly via React Router instead of linkElement.click().
                  // Calling .click() on the <a> inside a setTimeout is unreliable because
                  // React may re-render and replace the DOM node, causing the detached <a>
                  // to trigger a full-page navigation instead of a client-side route change.
                  if (navigator.vibrate) {
                    navigator.vibrate(10);
                  }
                  const route = app.app.route_prefix;
                  if (onAppClick) {
                    onAppClick();
                  }
                  if (route && route !== '#') {
                    navigate(route);
                  }
                  
                  handleLongPressEnd();
                }
              }}
              onTouchCancel={(e) => {
                if (!isEditMode) {
                  e.currentTarget.style.transform = '';
                  touchMovedRef.current = false;
                  
                  // Only reset if menu is NOT showing
                  if (!contextMenuShouldShowRef.current && !isShowingContextMenu) {
                    handleLongPressEnd();
                  }
                }
              }}
              onContextMenu={(e) => {
                // Prevent iOS/Safari context menu
                e.preventDefault();
                return false;
              }}
            >
              {customAppIcons[app.app.slug] ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  <img
                    src={customAppIcons[app.app.slug]}
                    alt={getAppDisplayName(app)}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                  <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 0 0.5px rgba(255,255,255,0.15), inset 0 1px 0 rgba(255,255,255,0.1)', borderRadius: 'inherit' }} />
                </div>
              ) : (
                <>
                  <div className="text-white mb-1 relative inline-block transition-transform duration-300">
                    <div className="transform transition-transform duration-300 group-hover:scale-110">
                      {getPlaceholderIcon(app.app.slug)}
                    </div>
                  </div>
                  <span className="text-[10px] text-white text-center font-medium leading-tight">
                    {getAppDisplayName(app)}
                  </span>
                </>
              )}
            </Link>
              {app.app.slug === 'payroll' && pendingPayrollCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center shadow-lg z-[9999999] animate-pulse pointer-events-none">
                  {pendingPayrollCount > 99 ? '99+' : pendingPayrollCount}
                </span>
              )}
              {app.app.slug === 'crm' && unreadLeadsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center shadow-lg z-[9999999] animate-pulse pointer-events-none">
                  {unreadLeadsCount > 99 ? '99+' : unreadLeadsCount}
                </span>
              )}
              <span className="text-[11px] text-white/90 text-center font-normal leading-tight mt-1.5 truncate w-full" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                {getAppDisplayName(app)}
              </span>
              </>
          ) : (
            <div 
              className={`aspect-square w-full ${
                isEditMode ? 'bg-gray-800/20 border-2 border-dashed border-gray-600 rounded-[22%] cursor-pointer hover:bg-gray-700/30 transition-colors' : ''
              }`}
              onClick={() => {
                if (isEditMode) {
                  handleEmptySlotClick(index, gridType);
                }
              }}
            >
              {/* Empty slot */}
            </div>
          )}
        </div>
      ))}
    </div>
    );
  };

  return (
    <div 
      className="w-full text-white flex flex-col overflow-x-hidden relative" 
      style={{ 
        minHeight: 'fit-content',
        zIndex: '999999 !important',
        position: 'relative',
        background: 'transparent',
      }}
    >
      {/* Nexus Connect */}
      {canViewNexusConnect && (
        <div 
          className="px-4 pt-4 pb-2 mb-1 flex justify-center"
          style={{
            animation: isOpen 
              ? `iosAppIconEnter 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both`
              : 'none',
          }}
        >
          <Link 
            to="/app-store"
            className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl transition-all duration-200 ease-out hover:-translate-y-0.5 active:scale-95"
            style={{
              background: 'linear-gradient(135deg, #7DD3FC 0%, #42A5F5 33%, #2979D6 66%, #1A56B8 100%)',
              boxShadow: '0 2px 8px rgba(61,162,242,0.3), 0 8px 24px rgba(61,162,242,0.15)',
            }}
            onClick={() => {
              if (onAppClick) {
                onAppClick();
              }
            }}
          >
            <img 
              src="/nexus-connect-icon.png" 
              alt="Nexus" 
              className="h-5 w-5 rounded-md object-cover"
            />
            <span className="text-sm font-semibold text-white tracking-wide">Nexus Connect</span>
          </Link>
        </div>
      )}

      {/* Navigation Items */}
      <nav className="pb-4 relative" style={{ zIndex: 999999 }}>
        {isUsingAppSystem ? (
          <div className="py-4">
            {loading ? (
              <div className="px-4 py-3 text-xs text-gray-500 italic">
                Loading apps...
              </div>
            ) : (
              <div className="px-4 relative" style={{ zIndex: 999999 }}>
                {/* Visible Apps Grid */}
                {renderGrid(visibleGridApps, 'visible')}
                
                {/* Hidden Apps Button - Show in normal mode if there are hidden apps */}
                {!isEditMode && hiddenApps.length > 0 && (
                  <div 
                    className="mt-4"
                    style={{
                      animation: isOpen 
                        ? `iosAppIconEnter 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both`
                        : 'none',
                    }}
                  >
                    <button
                      className="w-full text-center px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 backdrop-blur-sm active:scale-95"
                      onClick={() => {
                        setShowHiddenApps(!showHiddenApps);
                        if (navigator.vibrate) {
                          navigator.vibrate(10);
                        }
                      }}
                    >
                      <div className="flex-1 h-px bg-gray-600/50"></div>
                      <span className="flex items-center gap-2">
                        Hidden Apps
                        <Icons.ChevronDown 
                          className={`h-3 w-3 transition-transform duration-300 ${
                            showHiddenApps ? 'rotate-180' : ''
                          }`} 
                        />
                      </span>
                      <div className="flex-1 h-px bg-gray-600/50"></div>
                    </button>
                  </div>
                )}
                
                {/* Hidden Apps Section - Show in normal mode when expanded OR in edit mode */}
                {((!isEditMode && showHiddenApps) || isEditMode) && hiddenGridApps.length > 0 && (
                  <>
                    {/* Divider - Only show in edit mode since normal mode has the button */}
                    {isEditMode && (
                      <div className="my-6 flex items-center">
                        <div className="flex-1 h-px bg-gray-600"></div>
                        <span className="px-3 text-xs text-gray-400 font-medium">Hidden Apps</span>
                        <div className="flex-1 h-px bg-gray-600"></div>
                      </div>
                    )}
                    
                    {/* Hidden Apps Grid */}
                    <div className={isEditMode ? "" : "mt-3"}>
                      {renderGrid(hiddenGridApps, 'hidden')}
                    </div>
                  </>
                )}
                
                {/* Edit Mode Controls */}
                {isEditMode && (
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={saveLayout}
                      className="flex-1 bg-blue-600/90 hover:bg-blue-700/90 backdrop-blur-sm text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
                    >
                      Save Layout
                    </button>
                    <button
                      onClick={exitEditMode}
                      className="flex-1 bg-gray-600/90 hover:bg-gray-700/90 backdrop-blur-sm text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-gray-500">
            <p className="text-sm">App system not enabled</p>
            <p className="text-xs mt-2 opacity-75">Enable app system to see grid</p>
          </div>
        )}
      </nav>

       {/* Backdrop Overlay - Captures clicks to close mode when clicking outside */}
       {/* Lower z-index to ensure selected app icon appears above it */}
       {isShowingContextMenu && createPortal(
         <div
           className="fixed inset-0 z-[99997]"
           style={{ 
             pointerEvents: 'auto',
             background: 'rgba(0, 0, 0, 0.2)',
             backdropFilter: 'blur(10px)',
             WebkitBackdropFilter: 'blur(10px)',
             animation: 'iosBackdropFadeIn 0.3s cubic-bezier(0.4, 0.0, 0.2, 1) forwards',
             cursor: 'pointer',
           }}
           onClick={(e) => {
             // CRITICAL: Check if click originated from app icon or floating buttons
             // In PWA, touch events trigger click events, so we need to check the target
             const target = e.target as Element;
             
             // Don't close if clicking on floating buttons
             if (target.closest('[data-floating-buttons]')) {
               console.log('🎯 Backdrop click ignored - clicked on floating buttons');
               return;
             }
             
             // Don't close if clicking on selected app icon
             if (target.closest('[data-selected-app-icon]')) {
               console.log('🎯 Backdrop click ignored - clicked on selected app icon');
               return;
             }
             
             // Don't close if clicking on any app icon link
             if (target.closest('a[data-app-id]')) {
               console.log('🎯 Backdrop click ignored - clicked on app icon');
               return;
             }
             
             // Don't close if this click happened too soon after menu appeared (touch->click conversion)
             const timeSinceMenuAppeared = Date.now() - (contextMenuApp?.timestamp || 0);
             if (timeSinceMenuAppeared < 600) {
               console.log('🎯 Backdrop click ignored - too soon after menu appeared (likely touch->click conversion)');
               return;
             }
             
             // Don't close if a long press just completed (ignore touch->click conversion)
             const timeSinceLongPress = lastLongPressTimeRef.current ? Date.now() - lastLongPressTimeRef.current : Infinity;
             if (timeSinceLongPress < 600) {
               console.log('🎯 Backdrop click ignored - too soon after long press (touch->click conversion)');
               return;
             }
             
             // Only close on actual backdrop clicks (not touch-triggered clicks from app icons)
             // Check if the click target is actually the backdrop div itself
             if (target === e.currentTarget) {
               console.log('🟥 Backdrop clicked - closing menu');
               e.stopPropagation();
               setContextMenuApp(null);
               setSelectedAppForFeedback(null);
               setSelectedAppIconPosition(null);
               setIsShowingContextMenu(false);
               setIsLongPressTriggered(false);
               contextMenuShouldShowRef.current = false;
             } else {
               console.log('🎯 Backdrop click ignored - not clicking backdrop itself');
             }
           }}
           onTouchStart={(e) => {
             // Prevent touch events on backdrop from doing anything
             // The touch should be handled by the app icon's onTouchEnd
             e.stopPropagation();
           }}
           onTouchEnd={(e) => {
             // Prevent touch events on backdrop from closing menu
             // Only mouse clicks should close it
             e.stopPropagation();
           }}
         />,
         document.body
       )}

       {/* Selected App Icon - Rendered via Portal above blur backdrop */}
       {selectedAppForFeedback && selectedAppIconPosition && createPortal(
         <div
           data-selected-app-icon
           className="fixed z-[1000000] pointer-events-none"
           style={{
             left: `${selectedAppIconPosition.x}px`,
             top: `${selectedAppIconPosition.y}px`,
             width: `${selectedAppIconPosition.width}px`,
             height: `${selectedAppIconPosition.height}px`,
             transform: 'scale(1.25)',
             transformOrigin: 'center center',
             transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
           }}
           onClick={(e) => {
             e.stopPropagation();
           }}
         >
           {customAppIcons[selectedAppForFeedback.app.slug] ? (
             <div className="w-full h-full bg-white rounded-[22%] overflow-hidden relative" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.15), 0 12px 32px rgba(0,0,0,0.1)' }}>
               <img
                 src={customAppIcons[selectedAppForFeedback.app.slug]}
                 alt={getAppDisplayName(selectedAppForFeedback)}
                 className="w-full h-full object-cover"
                 draggable={false}
               />
               {selectedAppForFeedback.app.slug === 'payroll' && pendingPayrollCount > 0 && (
                 <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg z-[9999999] animate-pulse">
                   {pendingPayrollCount > 99 ? '99+' : pendingPayrollCount}
                 </span>
               )}
               {selectedAppForFeedback.app.slug === 'crm' && unreadLeadsCount > 0 && (
                 <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg z-[9999999] animate-pulse">
                   {unreadLeadsCount > 99 ? '99+' : unreadLeadsCount}
                 </span>
               )}
             </div>
           ) : (
             <div className="w-full h-full bg-gradient-to-br from-blue-600 to-blue-500 rounded-[22%] flex flex-col items-center justify-center p-2" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.15), 0 12px 32px rgba(0,0,0,0.1)' }}>
               <div className="text-white mb-1 relative inline-block">
                 {getPlaceholderIcon(selectedAppForFeedback.app.slug)}
                 {selectedAppForFeedback.app.slug === 'payroll' && pendingPayrollCount > 0 && (
                   <span className="absolute -top-3 -right-5 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg z-[9999999] animate-pulse">
                     {pendingPayrollCount > 99 ? '99+' : pendingPayrollCount}
                   </span>
                 )}
                 {selectedAppForFeedback.app.slug === 'crm' && unreadLeadsCount > 0 && (
                   <span className="absolute -top-3 -right-5 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg z-[9999999] animate-pulse">
                     {unreadLeadsCount > 99 ? '99+' : unreadLeadsCount}
                   </span>
                 )}
               </div>
               <span className="text-[10px] text-white text-center font-medium leading-tight">
                 {getAppDisplayName(selectedAppForFeedback)}
               </span>
             </div>
           )}
         </div>,
         document.body
       )}

       {/* Floating Action Buttons - Rendered via Portal above blur backdrop */}
       {contextMenuApp && selectedAppIconPosition && createPortal(
         <div
           data-floating-buttons
           className="fixed z-[999999] pointer-events-none"
           style={{
             // If buttons would overflow right edge, position to the left of the icon instead
             ...(selectedAppIconPosition.x + selectedAppIconPosition.width + 12 + 48 > window.innerWidth
               ? { left: `${selectedAppIconPosition.x - 48 - 12}px` }
               : { left: `${selectedAppIconPosition.x + selectedAppIconPosition.width + 12}px` }
             ),
             top: `${selectedAppIconPosition.y + (selectedAppIconPosition.height / 2) - 52}px`,
             display: 'flex',
             flexDirection: 'column',
             gap: '8px',
             pointerEvents: 'auto',
             animation: 'iosAppIconEnter 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
           }}
           onClick={(e) => {
             // Prevent clicks on buttons from closing the mode
             e.stopPropagation();
           }}
         >
          {canViewAppSettings && (
            <>
              {/* Settings Button */}
              <button
                className="bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/90 rounded-xl p-3 shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center active:scale-95 border border-gray-600/50"
                style={{
                  width: '48px',
                  height: '48px',
                  minWidth: '48px',
                  minHeight: '48px',
                }}
                onClick={() => {
                  if (navigator.vibrate) navigator.vibrate(10);
                  setSettingsApp(contextMenuApp);
                  setShowSettingsModal(true);
                  setContextMenuApp(null);
                  setSelectedAppForFeedback(null);
                  setSelectedAppIconPosition(null);
                  setIsShowingContextMenu(false);
                  setIsLongPressTriggered(false);
                  contextMenuShouldShowRef.current = false;
                }}
                title="Settings"
              >
                <Icons.Settings className="h-5 w-5 text-white" />
              </button>
              
              {/* Edit Layout Button */}
              <button
                className="bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/90 rounded-xl p-3 shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center active:scale-95 border border-gray-600/50"
                style={{
                  width: '48px',
                  height: '48px',
                  minWidth: '48px',
                  minHeight: '48px',
                }}
                onClick={() => {
                  if (navigator.vibrate) navigator.vibrate(10);
                  enterEditMode();
                  setSelectedAppForFeedback(null);
                  setSelectedAppIconPosition(null);
                  setIsShowingContextMenu(false);
                  setIsLongPressTriggered(false);
                  contextMenuShouldShowRef.current = false;
                }}
                title="Edit Layout"
              >
                <Icons.Edit3 className="h-5 w-5 text-white" />
              </button>
            </>
          )}
         </div>,
         document.body
       )}

      {/* App Settings Modal - Rendered via Portal to avoid z-index issues */}
      {showSettingsModal && settingsApp && createPortal(
        <AppSettingsModal
          app={settingsApp}
          isOpen={showSettingsModal}
          onClose={() => {
            setShowSettingsModal(false);
            setSettingsApp(null);
          }}
        />,
        document.body
      )}
    </div>
  );
}